import { useCallback, useEffect, useRef, useState } from 'react';
import { dailyPlanApi, rhythmApi } from '../api/services';
import type {
  CloseDayResponse,
  DailyPlan,
  PlanStatus,
  StartMyDayResponse,
} from '../types/models';

// ---------------------------------------------------------------------------
// usePlanState — single source of truth for the unified plan screen.
//
// Responsibilities:
//   1. On mount, fetch the existing plan record (lightweight) so the page can
//      render a status chip and tailor the call-to-action without paying the
//      cost of a full Start-My-Day cycle.
//   2. Expose the state-transition actions (start, replan, approve, activate,
//      close, planTomorrow) in one place so the page doesn't have to wire
//      each one separately.
//   3. Derive `canApprove / canActivate / canReplan / canClose` from the
//      current status so gated buttons don't have to re-state the rules.
//
// The hook does not auto-run Start-My-Day on mount — Start-My-Day writes to
// the DB, fires webhooks, and runs the AI planner. It is an intentional ritual,
// not a page-load side-effect.
// ---------------------------------------------------------------------------

export interface PlanState {
  // Full day data — populated only by an explicit start/replan action.
  data: StartMyDayResponse | null;
  // Lightweight plan record — populated on mount and after every transition.
  plan: DailyPlan | null;
  status: PlanStatus | null;

  // Initial mount fetch of the plan record.
  initialLoading: boolean;
  // Any long-running operation (start, replan, approve, activate, close).
  busy: boolean;
  error: string | null;

  // Most recent close-day result — let the page render the reflection capture.
  closeResult: CloseDayResponse | null;

  // Most recent start/replan outcome — drives the "Replan complete" success
  // alert. Cleared by `clearReplanOutcome()` when the user dismisses it, and
  // refreshed on every new replan call. Null while initialLoading or before
  // the first action.
  replanOutcome: {
    kind: 'start' | 'replan';
    promotedCount: number;
    backlogRemaining: number;
    scheduledCount: number;
  } | null;

  // Gates for the action buttons.
  canSendForApproval: boolean;
  canApprove: boolean;
  canActivate: boolean;
  canReplan: boolean;
  canClose: boolean;

  // Actions.
  start: () => Promise<void>;
  replan: () => Promise<void>;
  approve: () => Promise<void>;
  activate: () => Promise<void>;
  close: (reflection?: {
    reflectionContent?: string;
    insights?: string;
    adjustmentsMade?: string;
  }) => Promise<void>;
  planTomorrow: () => Promise<void>;

  clearError: () => void;
  clearCloseResult: () => void;
  clearReplanOutcome: () => void;
}

function statusOf(plan: DailyPlan | null): PlanStatus | null {
  return plan?.status ?? null;
}

export function usePlanState(date: string): PlanState {
  const [data, setData] = useState<StartMyDayResponse | null>(null);
  const [plan, setPlan] = useState<DailyPlan | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closeResult, setCloseResult] = useState<CloseDayResponse | null>(null);
  const [replanOutcome, setReplanOutcome] = useState<PlanState['replanOutcome']>(null);

  // Guard against setState-on-unmounted when the date changes quickly.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Lightweight plan record on mount. getByDate returns 404 when there's no
  // plan yet — swallow that and leave plan = null.
  useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    dailyPlanApi
      .getByDate(date)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch((e: any) => {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setPlan(null);
        } else {
          setError(e?.response?.data?.error ?? 'Unable to load plan.');
        }
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  // Cross-client sync — when another client mutates a work item that
  // belongs to today's plan, refetch the lightweight plan record so the
  // capacity strip + status chip stay accurate. We DON'T auto-refetch the
  // heavy StartMyDayResponse (would silently regenerate the schedule);
  // the user has to press Replan for that. Plain getByDate is cheap.
  //
  // Date filtering: skip the refetch when the broadcast targets a different
  // day. The handler reads detail.targetDate from the payload:
  //  - Single-item events (created/updated): refetch only if it matches.
  //  - Bulk-updated: refetch if ANY item.targetDate matches.
  //  - Missing targetDate (e.g. deletion that didn't carry it): refetch
  //    to be safe — better a wasted query than a stale plan.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;
      let shouldRefetch: boolean;
      if (detail.event === 'bulk-updated') {
        const items: Array<{ targetDate?: string }> = detail.items ?? [];
        shouldRefetch = items.length === 0 || items.some(
          (i) => !i.targetDate || i.targetDate === date,
        );
      } else {
        shouldRefetch = !detail.targetDate || detail.targetDate === date;
      }
      if (!shouldRefetch) return;
      dailyPlanApi.getByDate(date)
        .then((p) => { if (mountedRef.current) setPlan(p); })
        .catch(() => { /* 404 / network — leave existing state */ });
    };
    window.addEventListener('cadence:workitem-changed', handler);
    return () => window.removeEventListener('cadence:workitem-changed', handler);
  }, [date]);

  const runLoad = useCallback(
    async (kind: 'start' | 'replan') => {
      setBusy(true);
      setError(null);
      try {
        const res =
          kind === 'start'
            ? await rhythmApi.startMyDay({ date })
            : await rhythmApi.replan({ date });
        if (!mountedRef.current) return;
        setData(res);
        setPlan(res.plan);
        // Visible feedback for the user — without this, a no-op Replan looks
        // identical to a broken button. promotedFromBacklogCount is sourced
        // from the backend's auto-promote counter on this exact call.
        setReplanOutcome({
          kind,
          promotedCount: res.promotedFromBacklogCount ?? 0,
          backlogRemaining: res.availableBacklogCount ?? 0,
          scheduledCount: res.scheduledBlocks?.length ?? 0,
        });
      } catch (e: any) {
        if (!mountedRef.current) return;
        setError(e?.response?.data?.error ?? "Unable to load today's plan.");
      } finally {
        if (mountedRef.current) setBusy(false);
      }
    },
    [date],
  );

  const start = useCallback(() => runLoad('start'), [runLoad]);
  const replan = useCallback(() => runLoad('replan'), [runLoad]);

  const approve = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await dailyPlanApi.approve(date);
      if (!mountedRef.current) return;
      setPlan(updated);
      setData((d) => (d ? { ...d, plan: updated } : d));
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e?.response?.data?.error ?? 'Unable to approve the plan.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [date]);

  const activate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await dailyPlanApi.activate(date);
      if (!mountedRef.current) return;
      setPlan(updated);
      setData((d) => (d ? { ...d, plan: updated } : d));
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e?.response?.data?.error ?? 'Unable to activate the plan.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [date]);

  const close = useCallback(
    async (reflection?: {
      reflectionContent?: string;
      insights?: string;
      adjustmentsMade?: string;
    }) => {
      setBusy(true);
      setError(null);
      try {
        const res = await rhythmApi.closeDay({ date, ...(reflection ?? {}) });
        if (!mountedRef.current) return;
        setCloseResult(res);
        if (res.plan) {
          setPlan(res.plan);
          setData((d) => (d && res.plan ? { ...d, plan: res.plan } : d));
        }
      } catch (e: any) {
        if (!mountedRef.current) return;
        setError(e?.response?.data?.error ?? 'Unable to close the day.');
      } finally {
        if (mountedRef.current) setBusy(false);
      }
    },
    [date],
  );

  const planTomorrow = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await rhythmApi.planTomorrow();
      if (!mountedRef.current) return;
      // Tomorrow's plan is a different date — surface it as data so the page
      // can offer to jump there, but don't overwrite today's plan record.
      setData(res);
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e?.response?.data?.error ?? 'Unable to plan tomorrow.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const clearCloseResult = useCallback(() => setCloseResult(null), []);
  const clearReplanOutcome = useCallback(() => setReplanOutcome(null), []);

  const status = statusOf(plan);

  return {
    data,
    plan,
    status,
    initialLoading,
    busy,
    error,
    closeResult,
    canSendForApproval: status === 'Draft',
    canApprove: status === 'Proposed',
    canActivate: status === 'Approved',
    canReplan: status !== null && status !== 'Closed',
    canClose: status === 'Active' || status === 'Adjusted' || status === 'Approved',
    start,
    replan,
    approve,
    activate,
    close,
    planTomorrow,
    clearError,
    clearCloseResult,
    clearReplanOutcome,
    replanOutcome,
  };
}
